import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Link,
  Collapse,
  Slide,
  useMediaQuery
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  ExpandMore as ExpandMoreIcon,
  Cookie as CookieIcon
} from '@mui/icons-material';
import brandingConfig from '../../config/brandingConfig';

// Cookie-Einstellungen im localStorage speichern
const COOKIE_CONSENT_KEY = `${brandingConfig.appName.toLowerCase().replace(/\s/g, '_')}_cookie_consent`;

/**
 * Cookie-Hinweis als kompakte Karte unten links statt als breites Band.
 *
 * Die frühere Fassung war 1200 px breit und belegte am unteren Rand fast ein
 * Drittel des Fensters — auf der Landing Page verdeckte sie dauerhaft Inhalte.
 * Beide Auswahlmöglichkeiten sind bewusst gleich groß und gleich prominent.
 */
const BannerWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  zIndex: 1300,
  bottom: theme.spacing(3),
  left: theme.spacing(3),
  width: 400,
  maxWidth: `calc(100vw - ${theme.spacing(6)})`,
  [theme.breakpoints.down('sm')]: {
    bottom: 0,
    left: 0,
    right: 0,
    width: 'auto',
    maxWidth: 'none',
  },
}));

const CookieCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 20,
  color: theme.palette.common.white,
  backgroundColor: alpha('#141414', 0.96),
  backdropFilter: 'blur(14px)',
  border: `1px solid ${alpha('#ffffff', 0.12)}`,
  boxShadow: `0 24px 60px ${alpha('#000000', 0.45)}`,
  [theme.breakpoints.down('sm')]: {
    borderRadius: '20px 20px 0 0',
    borderBottom: 'none',
    // Home-Indicator auf iOS nicht überdecken
    paddingBottom: `calc(${theme.spacing(2.5)} + env(safe-area-inset-bottom))`,
  },
}));

const CookieBanner = () => {
  // `mounted` hält die Karte bis zum Ende der Ausblend-Animation im DOM.
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Beim Laden der Komponente prüfen, ob bereits Cookie-Einstellungen gespeichert sind
  useEffect(() => {
    const cookieConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!cookieConsent) {
      // Kurze Verzögerung für bessere UX
      const timer = setTimeout(() => {
        setMounted(true);
        setOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Cookie-Einstellungen speichern
  const handleAccept = (acceptAll = false) => {
    const consent = {
      necessary: true,
      functional: acceptAll,
      analytics: acceptAll,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setOpen(false);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  if (!mounted) return null;

  return (
    <Slide direction="up" in={open} appear onExited={() => setMounted(false)}>
      <BannerWrapper role="dialog" aria-modal="false" aria-label="Datenschutzeinstellungen">
        <CookieCard>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.16),
              }}
            >
              <CookieIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            </Box>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
              Datenschutz
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: alpha('#ffffff', 0.7), lineHeight: 1.7 }}
          >
            Wir verwenden Cookies für Anmeldung und Sessionverwaltung — keine
            Marketing- oder Analyse-Cookies von Drittanbietern.
          </Typography>

          <Collapse in={expanded} timeout="auto">
            <Typography
              variant="body2"
              sx={{ color: alpha('#ffffff', 0.55), lineHeight: 1.7, mt: 1.5 }}
            >
              Technisch notwendige Cookies halten Ihre Sitzung aufrecht und sind für den
              sicheren Betrieb erforderlich. Alles Weitere steht in der{' '}
              <Link
                component={RouterLink}
                to="/datenschutz"
                sx={{ color: 'primary.main', textDecorationColor: 'currentColor' }}
              >
                Datenschutzerklärung
              </Link>.
            </Typography>
          </Collapse>

          <Button
            onClick={handleExpandClick}
            aria-expanded={expanded}
            size="small"
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
                }}
              />
            }
            sx={{
              mt: 1,
              px: 0,
              color: alpha('#ffffff', 0.6),
              fontWeight: 500,
              '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
            }}
          >
            {expanded ? 'Weniger' : 'Mehr erfahren'}
          </Button>

          {/* Beide Optionen gleich gewichtet — keine Dark Pattern. */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              mt: 2,
              flexDirection: isMobile ? 'column-reverse' : 'row',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleAccept(false)}
              sx={{
                flex: 1,
                borderRadius: 999,
                py: 1,
                fontWeight: 600,
                color: '#fff',
                borderColor: alpha('#ffffff', 0.3),
                '&:hover': { borderColor: '#fff', bgcolor: alpha('#ffffff', 0.06) },
              }}
            >
              Nur notwendige
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAccept(true)}
              sx={{ flex: 1, borderRadius: 999, py: 1, fontWeight: 700 }}
            >
              Alle akzeptieren
            </Button>
          </Box>
        </CookieCard>
      </BannerWrapper>
    </Slide>
  );
};

export default CookieBanner;
